import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFilesModal from "../uploaders/UploadFilesModal";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";

import { deleteDoc, doc, getDocs, collection } from "firebase/firestore";
import { ref as storageRef, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import { statusColors } from "../constants/statusColors";
import TicketActivityHistoryMobile from "../components/TicketActivityHistoryMobile";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function EditTicketModalMobile({
  open,
  onClose,
  ticket,
  handleUpdate,
  loading,
  role,
  selectedProject,
}) {
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();

  // États locaux
  const [ticketName, setTicketName] = useState("");
  const [ticketType, setTicketType] = useState("");
  const [originalQuestion, setOriginalQuestion] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deletePhase, setDeletePhase] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // À la réception du ticket, on recalcule les états
  useEffect(() => {
    if (ticket) {
      setTicketName(ticket.ticketName);
      setTicketType(ticket.ticketType);
      setOriginalQuestion(ticket.originalQuestion);
      setSelectedPriority(ticket.priority);
      setTicketStatus(ticket.status);
      setAttachments((ticket.images || []).map((url) => ({ url, file: null })));
    }
  }, [ticket]);

  if (!ticket) return null;

  const statusOptions = [
    "Open",
    "Working on It",
    "In Staging",
    "In Test",
    "In Production",
    "In review",
    "Closed",
  ];

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      // 1) Supprimer attachments
      setDeletePhase("attachments");
      const folderRef = storageRef(storage, `tickets/${ticket.id}`);
      const listResult = await listAll(folderRef);
      await Promise.all(
        listResult.items.map((i) => deleteObject(i).catch(() => {}))
      );

      // 2) Supprimer activité
      setDeletePhase("activity");
      const actCol = collection(
        db,
        "projects",
        selectedProject,
        "tickets",
        ticket.id,
        "activity"
      );
      const snap = await getDocs(actCol);
      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref).catch(() => {})));

      // 3) Supprimer le ticket
      setDeletePhase("ticket");
      await deleteDoc(
        doc(db, "projects", selectedProject, "tickets", ticket.id)
      );

      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
      setDeletePhase("");
      setConfirmDeleteOpen(false);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar position="sticky">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {translate("UpdateTicket")}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setConfirmDeleteOpen(true)}
          >
            <DeleteIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {/* Numéro et statut */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("TicketNumber")}
          </Typography>
          <Chip label={ticket.ticketNumber} />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("Status")}
          </Typography>
          {role === "Admin" ? (
            <Select
              fullWidth
              value={ticketStatus}
              onChange={(e) => setTicketStatus(e.target.value)}
              sx={{
                bgcolor: statusColors[ticketStatus] || "#ccc",
                color: "#fff",
              }}
            >
              {statusOptions.map((s) => (
                <MenuItem
                  key={s}
                  value={s}
                  sx={{
                    bgcolor: statusColors[s] || "#ccc",
                    color: "#fff",
                  }}
                >
                  {translate(s)}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Chip
              label={translate(ticketStatus)}
              sx={{
                bgcolor: statusColors[ticketStatus] || "#ccc",
                color: "#fff",
              }}
            />
          )}
        </Box>

        {/* Nom du ticket */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("TicketNameForm")}
          </Typography>
          <TextField
            fullWidth
            value={ticketName}
            // onChange={(e) => setTicketName(e.target.value)}
            disabled
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("WriteDescription")}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={originalQuestion}
            //onChange={(e) => setOriginalQuestion(e.target.value)}
            disabled
          />
        </Box>

        {/* Priorité */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("Priority")}
          </Typography>
          <Select
            fullWidth
            value={selectedPriority}
            //onChange={(e) => setSelectedPriority(e.target.value)}
            disabled
          >
            {["Low", "Medium", "High"].map((lvl) => (
              <MenuItem key={lvl} value={lvl}>
                {translate(lvl)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Type */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("TicketType")}
          </Typography>
          <TextField
            fullWidth
            value={ticketType}
            //onChange={(e) => setTicketType(e.target.value)}
            disabled
          />
        </Box>

        {/* Pièces jointes */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {translate("AttachedFiles")}
          </Typography>
          <Button variant="outlined" onClick={() => setUploadModalOpen(true)}>
            {translate("ManageFile")}
          </Button>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mt: 1,
              overflowX: "auto",
            }}
          >
            {attachments.map((a, i) => (
              <Avatar key={i} variant="rounded" src={a.preview || a.url} />
            ))}
          </Box>
        </Box>

        {/* Historique */}
        <TicketActivityHistoryMobile
          ticket={ticket}
          role={role}
          selectedProject={selectedProject}
        />
      </Box>

      {/* Actions en bas */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid #ddd",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button onClick={onClose}>{translate("Cancel")}</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={() =>
            handleUpdate({
              ticketName,
              originalQuestion,
              ticketType,
              priority: selectedPriority,
              status: role === "Admin" ? ticketStatus : undefined,
              images: attachments,
            })
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            translate(role === "Admin" ? "UpdateTicket" : "SaveAttachments")
          )}
        </Button>
      </Box>

      {/* Upload modal */}
      <UploadFilesModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        files={attachments}
        setFiles={setAttachments}
      />

      {/* Confirmation suppression */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">
            {translate("ConfirmDeleteTicket")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            disabled={deleting}
          >
            {translate("Cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? translate("Deleting") : translate("Delete")}
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
}
